import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { PageHero } from '../components/PageHero';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getImageSrc = (url) => {
  if (!url) return '';
  // Cloudinary URLs (already absolute) - return as-is
  if (url.startsWith('https://res.cloudinary.com/')) return url;
  // Other absolute URLs - return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Legacy GridFS format
  if (url.startsWith('/api/media/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  if (url.startsWith('/api/uploads/')) return `${process.env.REACT_APP_BACKEND_URL}${url.replace('/api/uploads/', '/api/media/')}`;
  if (url.startsWith('/uploads/')) return `${process.env.REACT_APP_BACKEND_URL}/api/media/${url.replace('/uploads/', '')}`;
  return url;
};

const colorFilterStyle = (mode) => {
  if (mode === 'grayscale') return { filter: 'grayscale(100%)' };
  if (mode === 'black') return { filter: 'grayscale(100%) brightness(0)' };
  return {};
};

export const PartnersPage = () => {
  const { t } = useLanguage();
  const [partners, setPartners] = useState([]);
  const [logoColorMode, setLogoColorMode] = useState('original');
  const [siteImages, setSiteImages] = useState({ page_heroes: {} });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partnersRes, imagesRes] = await Promise.all([
          axios.get(`${API}/brand-partners`),
          axios.get(`${API}/site-images`)
        ]);
        setPartners(partnersRes.data.partners || []);
        setLogoColorMode(partnersRes.data.logo_color_mode || 'original');
        setSiteImages(imagesRes.data);
      } catch (err) {
        console.error('Error fetching partners:', err);
      }
    };
    fetchData();
  }, []);

  const yachtBrands = partners.filter(p => p.category === 'yacht');
  const motorBrands = partners.filter(p => p.category === 'motor');
  const accessoryBrands = partners.filter(p => p.category === 'accessory');
  const companyPartners = partners.filter(p => p.category === 'company');

  const renderCategory = (title, items, cols) => {
    if (items.length === 0) return null;
    return (
      <div className="mb-16">
        <h3 className="text-sm uppercase tracking-[0.2em] text-[#6E6E73] text-center mb-8">{title}</h3>
        <div className={`grid ${cols} gap-6 sm:gap-8`}>
          {items.map((brand, index) => (
            <motion.a
              key={brand.id}
              href={brand.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              data-testid={`partner-${brand.name.toLowerCase().replace(/\s/g, '-')}`}
              className="group flex items-center justify-center h-28 sm:h-32 bg-white border border-[#E5E5EA] hover:border-[#0B1221] hover:shadow-md transition-all duration-300 rounded-lg p-4"
            >
              <img
                src={getImageSrc(brand.logo)}
                alt={brand.name}
                className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                style={{
                  ...colorFilterStyle(logoColorMode),
                  maxWidth: '120px',
                  maxHeight: '80px'
                }}
              />
            </motion.a>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="partners-page" className="pt-20">
      {/* Hero */}
      <PageHero
        title={t('brands.title')}
        subtitle={t('brands.subtitle')}
        backgroundUrl={siteImages.page_heroes?.partners}
        darkMode={true}
      />

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">

          {renderCategory('Yacht', yachtBrands, 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5')}
          {renderCategory('Motori', motorBrands, 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4')}
          {renderCategory('Accessori', accessoryBrands, 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4')}
          {renderCategory('Aziende Partner', companyPartners, 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4')}
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
